import React, { useState, useRef, useEffect } from 'react';
import './GameDropdown.css';

const GameDropdown = ({ games, selectedGame, onGameChange, gameSettingsV2 }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleGameSelect = (game) => {
    onGameChange(game);
    setIsOpen(false);
  };

  // Sort games based on gameSettingsV2 if available
  const sortedGames = React.useMemo(() => {
    if (!games) return [];
    if (!gameSettingsV2 || !Array.isArray(gameSettingsV2)) return games;

    return [...games].sort((a, b) => {
      const settingA = gameSettingsV2.find(s => s.game_name === a);
      const settingB = gameSettingsV2.find(s => s.game_name === b);
      const orderA = settingA?.display_order ?? 999;
      const orderB = settingB?.display_order ?? 999;
      return orderA - orderB;
    });
  }, [games, gameSettingsV2]);

  // Game colors matching the sidebar/tile colors
  const gameColors = {
    'Magic: The Gathering': '#1a5fb4',
    'Pokemon': '#f5c211',
    'Yu-Gi-Oh!': '#9141ac',
    'Flesh and Blood': '#c01c28',
    'Lorcana': '#26a269',
    'One Piece': '#e66100',
    'Star Wars': '#2ec27e',
    'Sorcery': '#613583',
    'MetaZoo': '#3584e4',
    'Dragon Ball Super': '#ff7800',
    'Video Games': '#5e5c64'
  };

  const getGameColor = (game) => {
    if (gameColors[game]) return gameColors[game];
    // Generate consistent color from game name
    let hash = 0;
    for (let i = 0; i < game.length; i++) {
      hash = game.charCodeAt(i) + ((hash << 5) - hash);
    }
    const hue = Math.abs(hash % 360);
    return `hsl(${hue}, 60%, 45%)`;
  };

  return (
    <div className="game-dropdown-container" ref={dropdownRef}>
      <button
        className="game-dropdown-trigger"
        onClick={() => setIsOpen(!isOpen)}
        style={{
          backgroundColor: selectedGame ? getGameColor(selectedGame) : '#f0f0f0',
          borderColor: selectedGame ? getGameColor(selectedGame) : '#ccc',
          color: selectedGame ? 'white' : '#333'
        }}
      >
        <span className="game-dropdown-label">
          {selectedGame || 'Select Game'}
        </span>
        <svg
          className={`game-dropdown-arrow ${isOpen ? 'open' : ''}`}
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M6 9l6 6 6-6"/>
        </svg>
      </button>

      {isOpen && (
        <div className="game-dropdown-menu">
          {sortedGames.map((game) => (
            <button
              key={game}
              className={`game-dropdown-item ${game === selectedGame ? 'selected' : ''}`}
              onClick={() => handleGameSelect(game)}
              style={{
                backgroundColor: game === selectedGame ? getGameColor(game) : 'transparent',
                borderLeftColor: getGameColor(game),
                color: game === selectedGame ? 'white' : '#333'
              }}
            >
              <span
                className="game-color-indicator"
                style={{ backgroundColor: getGameColor(game) }}
              />
              {game}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default GameDropdown;
